<template>
  <transition name="sheet-slide">
    <div v-if="open" class="orders-sheet-wrapper" @click="$emit('close')">
      <div class="orders-sheet" @click.stop role="dialog" aria-modal="true">
        <!-- Modern Header with Gradient -->
        <div class="sheet-header">
          <div class="header-content">
            <div class="header-title">
              <font-awesome-icon icon="fa-solid fa-bell" class="header-icon"/>
              <h6>Online Orders</h6>
              <div class="orders-count" v-if="orders.length > 0">{{ orders.length }}</div>
            </div>
            <button class="btn-close" @click="$emit('close')" aria-label="Close">
              <font-awesome-icon icon="fa-solid fa-xmark"/>
            </button>
          </div>
          <div class="gradient-accent"></div>
        </div>

        <!-- Sheet Body with Custom Scrollbar -->
        <div class="sheet-body">
          <!-- Empty State -->
          <div v-if="orders.length === 0" class="empty-state">
            <div class="empty-icon">
              <font-awesome-icon icon="fa-solid fa-inbox" />
            </div>
            <p>No pending orders</p>
          </div>

          <!-- Orders List -->
          <transition-group name="order-slide" tag="div" v-else>
            <div v-for="order in orders" :key="order.id" class="order-card" @click="viewOrderDetails(order.id)">
              <!-- Status Badge -->
              <div class="status-badge" :class="`status-${getStatusType(order.status)}`">
                <font-awesome-icon :icon="getStatusIcon(order.status)" />
                <span>{{ formatStatusText(order.status) }}</span>
              </div>

              <!-- Order Info -->
              <div class="order-info">
                <div class="order-number">
                  <font-awesome-icon icon="fa-solid fa-hashtag" class="number-icon"/>
                  {{ order.orderNumber }}
                </div>
                <div class="order-meta">
                  <span class="customer-name">
                    <font-awesome-icon icon="fa-solid fa-user"/>
                    {{ order.customer?.name || 'Guest' }}
                  </span>
                  <span class="separator">•</span>
                  <span class="payment-method">
                    <font-awesome-icon :icon="getPaymentIcon(order.payment?.method)"/>
                    {{ order.payment?.method || 'Cash' }}
                  </span>
                </div>
              </div>

              <!-- Order Amount -->
              <div class="order-amount">{{ formatMoney(order.pricing?.totalAmount) }}</div>

              <!-- Items Count -->
<!--              <div class="items-count">-->
<!--                <font-awesome-icon icon="fa-solid fa-box"/>-->
<!--                {{ (order.items||[]).length }} item(s)-->
<!--              </div>-->

              <!-- Action Buttons - Compact Version -->
              <div class="action-buttons-compact">
                <button
                  class="action-btn btn-accept"
                  @click.stop="onAccept(order)"
                  :disabled="loadingOrders[order.id]?.action === 'accept'"
                  title="Accept"
                >
                  <font-awesome-icon v-if="loadingOrders[order.id]?.action === 'accept'" icon="fa-solid fa-spinner fa-spin" />
                  <font-awesome-icon v-else icon="fa-solid fa-check"/>
                </button>
                <button
                  v-if="order.metadata.status === 'PENDING'"
                  class="action-btn btn-adjust"
                  @click.stop="onAdjust(order)"
                  :disabled="loadingOrders[order.id]?.action === 'adjust'"
                  title="Adjust"
                >
                  <font-awesome-icon v-if="loadingOrders[order.id]?.action === 'adjust'" icon="fa-solid fa-spinner fa-spin" />
                  <font-awesome-icon v-else icon="fa-solid fa-edit"/>
                </button>
                <button
                  class="action-btn btn-reject"
                  @click.stop="onReject(order)"
                  :disabled="loadingOrders[order.id]?.action === 'reject'"
                  title="Reject"
                >
                  <font-awesome-icon v-if="loadingOrders[order.id]?.action === 'reject'" icon="fa-solid fa-spinner fa-spin" />
                  <font-awesome-icon v-else icon="fa-solid fa-times"/>
                </button>
              </div>
            </div>
          </transition-group>
        </div>

        <!-- Order Details Modal -->
        <OrderDetailsModal :show="showModal" :order="selectedOrderDetails" @close="closeModal"/>
        <!-- Adjust Order Modal -->
        <AdjustOrderModal
          :show="showAdjustModal"
          :order="orderToAdjust"
          @close="showAdjustModal = false"
          @adjust="onModalAdjustSubmit"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import OrderDetailsModal from '@/components/orders/OrderDetailsModal.vue';
import AdjustOrderModal from '@/components/orders/AdjustOrderModal.vue';

export default {
  name: 'OrdersSheet',
  components: {
    OrderDetailsModal,
    AdjustOrderModal,
  },
  props: {
    open: { type: Boolean, default: false },
    orders: { type: Array, default: () => [] }
  },
  data() {
    return {
      showModal: false,
      showAdjustModal: false,
      orderToAdjust: null,
      loadingOrders: {}, // Track which orders are being processed: { [orderId]: { action: 'accept'|'reject'|'adjust'|'update' } }
    };
  },
  computed: {
    selectedOrderDetails() {
      return this.$store.getters['orders/getSelectedOrder'];
    }
  },
  methods: {
    formatMoney(val) {
      try { return window.$utils.formatMoney(val || 0); } catch(e) { return val; }
    },
    formatStatusText(status) {
      if (!status) return '';
      return status.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
    },
    getStatusType(status) {
      const statusMap = {
        'PENDING': 'pending',
        'CONFIRMED': 'confirmed',
        'PREPARING': 'preparing',
        'READY': 'ready',
        'OUT_FOR_DELIVERY': 'delivering',
        'COMPLETED': 'completed',
        'CANCELED': 'canceled',
        'DENIED': 'denied'
      };
      return statusMap[status] || 'pending';
    },
    getStatusIcon(status) {
      const iconMap = {
        'PENDING': 'fa-solid fa-clock',
        'CONFIRMED': 'fa-solid fa-check-circle',
        'PREPARING': 'fa-solid fa-utensils',
        'READY': 'fa-solid fa-box-open',
        'OUT_FOR_DELIVERY': 'fa-solid fa-truck',
        'COMPLETED': 'fa-solid fa-check-double',
        'CANCELED': 'fa-solid fa-ban',
        'DENIED': 'fa-solid fa-times-circle'
      };
      return iconMap[status] || 'fa-solid fa-circle';
    },
    getPaymentIcon(method) {
      const methodLower = (method || '').toLowerCase();
      if (methodLower.includes('card') || methodLower.includes('credit') || methodLower.includes('debit')) {
        return 'fa-solid fa-credit-card';
      } else if (methodLower.includes('cash')) {
        return 'fa-solid fa-money-bill-wave';
      } else if (methodLower.includes('eft') || methodLower.includes('transfer')) {
        return 'fa-solid fa-university';
      }
      return 'fa-solid fa-wallet';
    },
    async onReject(order) {
      const predefinedReasons = ['Store closed', 'No stock', 'Customer cancelled', 'Payment not received'];

      const { value: reason } = await Swal.fire({
        title: 'Reject Order',
        html: `
          <input id="swal-reason-input" class="swal2-input" placeholder="Enter rejection reason">
          <div class="swal-predefined-reasons">
            ${predefinedReasons.map(r => `<button type="button" class="swal-reason-btn">${r}</button>`).join('')}
          </div>
          <style>
            .swal-predefined-reasons { margin-top: 1em; display: flex; flex-wrap: wrap; gap: 0.5em; justify-content: center; }
            .swal-reason-btn {
              background-color: var(--secondary-button-bg, #F1F5F9);
              border: 1px solid var(--card-border, #d1d5db);
              color: var(--text-primary, #1f2937);
              border-radius: 1em;
              padding: 0.4em 0.8em;
              font-size: 0.9em;
              cursor: pointer;
              transition: all 0.2s ease;
            }
            .swal-reason-btn:hover {
              background-color: var(--accent-electric-blue, #3399ff);
              color: white;
              border-color: var(--accent-electric-blue, #3399ff);
            }
          </style>
        `,
        showCancelButton: true,
        confirmButtonText: 'Reject',
        confirmButtonColor: '#FF3B30',
        didOpen: () => {
          const input = document.getElementById('swal-reason-input');
          document.querySelectorAll('.swal-reason-btn').forEach(btn => {
            btn.addEventListener('click', () => {
              input.value = btn.textContent;
            });
          });
        },
        preConfirm: () => {
          const reasonValue = document.getElementById('swal-reason-input').value;
          if (!reasonValue) {
            Swal.showValidationMessage('Please enter or select a reason');
            return;
          }
          return reasonValue;
        }
      });

      if (!reason) return;

      // Show loading state
      this.$set(this.loadingOrders, order.id, { action: 'reject' });
      this.$utils.showLoading('Rejecting order...');

      try {
        this.$emit('reject', { orderId: order.id, reason });
        // Wait a moment for the parent to process, then show success
        setTimeout(() => {
          this.$delete(this.loadingOrders, order.id);
          this.$utils.hideLoading();
          Swal.fire({
            icon: 'success',
            title: 'Order Rejected',
            text: `Order #${order.orderNumber} has been rejected.`,
            timer: 2000,
            showConfirmButton: false
          });
        }, 500);
      } catch (error) {
        console.error('Error rejecting order:', error);
        this.$delete(this.loadingOrders, order.id);
        this.$utils.hideLoading();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Reject Order',
          text: error.message || 'An error occurred while rejecting the order.',
        });
      }
    },
    async onAdjust(order) {
      // Show loading state
      this.$set(this.loadingOrders, order.id, { action: 'adjust' });
      this.$utils.showLoading('Loading order details...');

      try {
        await this.$store.dispatch('orders/fetchOrderDetails', order.id);
        this.orderToAdjust = this.selectedOrderDetails;
        this.$utils.hideLoading();

        if (!this.orderToAdjust || !this.orderToAdjust.items || this.orderToAdjust.items.length === 0) {
          return Swal.fire('No Items', 'This order has no items to adjust or failed to load.', 'info');
        }

        this.showAdjustModal = true;
      } catch (error) {
        console.error('Error loading order details:', error);
        this.$utils.hideLoading();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Load Order',
          text: error.message || 'An error occurred while loading order details.',
        });
      } finally {
        this.$delete(this.loadingOrders, order.id);
      }
    },
    onModalAdjustSubmit(payload) {
      const orderId = this.orderToAdjust?.id;
      this.showAdjustModal = false;
      this.orderToAdjust = null;
      this.$emit('adjust', { orderId, payload });
    },
    async onAccept(order) {
      // Show loading state
      this.$set(this.loadingOrders, order.id, { action: 'accept' });
      this.$utils.showLoading('Accepting order...');

      try {
        await this.$store.dispatch('orders/acceptOnlineOrder', order);
        // Wait a moment for the parent to process, then show success
        setTimeout(() => {
          this.$delete(this.loadingOrders, order.id);
          this.$utils.hideLoading();
          Swal.fire({
            icon: 'success',
            title: 'Order Accepted!',
            text: `Order #${order.orderNumber} has been accepted successfully.`,
            timer: 2000,
            showConfirmButton: false
          });
        }, 500);
      } catch (error) {
        console.error('Error accepting order:', error);
        this.$delete(this.loadingOrders, order.id);
        this.$utils.hideLoading();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Accept Order',
          text: error.message || 'An error occurred while accepting the order.',
        });
      }
    },
    async onUpdateStatus(order) {
      const { value: status } = await Swal.fire({
        title: 'Update Status',
        input: 'select',
        inputOptions: {
          CONFIRMED: 'CONFIRMED',
          PREPARING: 'PREPARING',
          READY: 'READY',
          OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
          COMPLETED: 'COMPLETED',
          CANCELED: 'CANCELED',
        },
        inputPlaceholder: 'Select status',
        showCancelButton: true
      });
      if (!status) return;
      const { value: notes } = await Swal.fire({
        title: 'Notes (optional)',
        input: 'text',
        inputPlaceholder: 'Add notes'
      });

      // Show loading state
      this.$set(this.loadingOrders, order.id, { action: 'update' });
      this.$utils.showLoading('Updating order status...');

      try {
        this.$emit('update-status', { orderId: order.id, status, notes });
        // Wait a moment for the parent to process, then show success
        setTimeout(() => {
          this.$delete(this.loadingOrders, order.id);
          this.$utils.hideLoading();
          Swal.fire({
            icon: 'success',
            title: 'Status Updated',
            text: `Order #${order.orderNumber} status updated to ${status}.`,
            timer: 2000,
            showConfirmButton: false
          });
        }, 500);
      } catch (error) {
        console.error('Error updating order status:', error);
        this.$delete(this.loadingOrders, order.id);
        this.$utils.hideLoading();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Update Status',
          text: error.message || 'An error occurred while updating the order status.',
        });
      }
    },
    async viewOrderDetails(orderId) {
      await this.$store.dispatch('orders/fetchOrderDetails', orderId);
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    }
  }
}
</script>


<style lang="scss" scoped>
/* ========== SHEET WRAPPER & BACKDROP ========== */
.orders-sheet-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* ========== SHEET CONTAINER ========== */
.orders-sheet {
  position: relative;
  width: 420px;
  max-width: 90vw;
  height: 100%;
  background: var(--card-bg);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
  }
}

/* ========== SHEET HEADER ========== */
.sheet-header {
  position: relative;
  flex-shrink: 0;
  padding: 24px;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  /* Subtle pattern overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 25%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 25%);
    opacity: 0.6;
    z-index: 0;
  }

  .gradient-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  .header-icon {
    font-size: 1.25rem;
    animation: pulse 2s infinite;
  }

  h6 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .orders-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 14px;
    font-size: 0.9rem;
    font-weight: 700;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
    animation: fadeIn 0.4s ease;
  }
}

.btn-close {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

/* ========== SHEET BODY ========== */
.sheet-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-electric-blue-rgb), 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--accent-electric-blue-rgb), 0.5);
  }
}

/* ========== EMPTY STATE ========== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  animation: fadeIn 0.5s ease;

  .empty-icon {
    font-size: 3rem;
    color: var(--text-secondary);
    opacity: 0.3;
    margin-bottom: 16px;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    margin: 0;
  }
}

/* ========== ORDER CARD ========== */
.order-card {
  background: var(--secondary-button-bg);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
  border: 2px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeIn 0.4s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(var(--accent-electric-blue-rgb), 0.2);
    transform: translateX(-4px);
    box-shadow: var(--card-shadow);
  }
}

/* ========== STATUS BADGE ========== */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 16px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-pending {
    background: linear-gradient(135deg, var(--warning) 0%, #FFB84D 100%);
    color: white;
  }

  &.status-confirmed {
    background: linear-gradient(135deg, var(--accent-electric-blue) 0%, #5BB3FF 100%);
    color: white;
  }

  &.status-preparing {
    background: linear-gradient(135deg, var(--accent-neon-purple) 0%, #BD5AFF 100%);
    color: white;
  }

  &.status-ready {
    background: linear-gradient(135deg, var(--success) 0%, #5EE47B 100%);
    color: white;
  }

  &.status-delivering {
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: white;
  }

  &.status-completed {
    background: linear-gradient(135deg, #28C76F 0%, #1F9D57 100%);
    color: white;
  }

  &.status-canceled,
  &.status-denied {
    background: linear-gradient(135deg, var(--danger) 0%, #FF6B6B 100%);
    color: white;
  }
}

/* ========== ORDER INFO ========== */
.order-info {
  margin-bottom: 12px;
}

.order-number {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;

  .number-icon {
    color: var(--accent-electric-blue);
    font-size: 0.9rem;
  }
}

.order-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary);

  .customer-name,
  .payment-method {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .separator {
    opacity: 0.5;
  }
}

.order-amount {
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
}

.items-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

/* ========== ACTION BUTTONS - COMPACT ========== */
.action-buttons-compact {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.action-btn {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &.btn-accept {
    background: linear-gradient(135deg, var(--success) 0%, #5EE47B 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 10px rgba(var(--success-rgb), 0.3);
    }
  }

  &.btn-adjust {
    background: linear-gradient(135deg, var(--warning) 0%, #FFB84D 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 10px rgba(var(--accent-orange-rgb), 0.3);
    }
  }

  &.btn-update {
    background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 10px rgba(var(--accent-electric-blue-rgb), 0.3);
    }
  }

  &.btn-reject {
    background: linear-gradient(135deg, var(--danger) 0%, #FF6B6B 100%);
    color: white;

    &:hover {
      box-shadow: 0 4px 10px rgba(var(--danger-rgb), 0.3);
    }
  }
}

/* ========== ANIMATIONS ========== */
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Sheet Slide Transition */
.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: opacity 0.3s ease;

  .orders-sheet {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.sheet-slide-enter-from,
.sheet-slide-leave-to {
  opacity: 0;

  .orders-sheet {
    transform: translateX(100%);
  }
}

/* Order List Transitions */
.order-slide-enter-active,
.order-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.order-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.order-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.order-slide-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ========== RESPONSIVE ========== */
@media (max-width: 640px) {
  .orders-sheet {
    width: 100%;
  }

  .sheet-header {
    padding: 20px;
  }

  .sheet-body {
    padding: 16px;
  }

  .header-title h6 {
    font-size: 1.1rem;
  }
}

/* Styles for the adjustment modal */
:deep(.swal-adjust-form) {
  text-align: left;
  max-height: 60vh;
  overflow-y: auto;
  padding: 1em;
}

:deep(.swal-adjust-item) {
  background: rgba(0,0,0,0.05);
  border-radius: 8px;
  padding: 1em;
  margin-bottom: 1em;
  border: 1px solid rgba(0,0,0,0.1);
}

:deep(.item-name) {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5em;
}

:deep(.item-controls) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1em;
}

:deep(.item-controls .swal2-select),
:deep(.item-controls .swal2-input) {
  width: 100%;
}

:deep(.item-controls .item-reason) {
  grid-column: 1 / -1; /* Span across both columns */
}
</style>
